import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClockArrowDown } from "lucide-react";

import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import DateRangeFilterInput from "../../../components/Filter/DateRangeFilterInput";
import ActivityLogTable from "../../../components/ActivityLog/ActivityLogTable";
import ActivityLogExportModal from "../../../components/ActivityLog/ActivityLogExportModal";
import {
  UIButton,
  UIEmptyState,
  UIPaginationBar,
} from "../../../components/ui";
import {
  ACTIVITY_MODULE_BY_API_KEY,
  ADMIN_ACTIVITY_MODULES,
  formatActivityDateTime,
  toActivityLogRow,
} from "../../../components/Dashboard/RecentActivityLog/constants";
import useDebounce from "../../../hooks/useDebounce";
import { getActivityLogs } from "../../../network/apis/activityLogs";
import { getActivityLogsRequest } from "../../../store/ActivityLogs/actions";
import { getApprovedCompaniesRequest } from "../../../store/Lookups/actions";
import "../../../components/ActivityLog/ActivityLog.scss";

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 450;

const API_KEY_BY_MODULE = Object.fromEntries(
  Object.entries(ACTIVITY_MODULE_BY_API_KEY).map(([key, id]) => [id, key]),
);

/**
 * Super Admin "Activity Log" (Full Logs) — every admin activity entry of the
 * platform, newest first (GET /activity_logs, PROLINE-3943): search by actor
 * or company name, filter by module (multi), company (single) and date range,
 * paginate, and export the matching entries. Entries are read-only.
 */
const ActivityLog = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { list, meta, loading } = useSelector((state) => state.activityLogs);
  const companies = useSelector((state) => state.lookups.approvedCompanies);
  const { activityLog: copy, dashboard, shared } = messages[lang];
  const moduleLabels = dashboard.recentActivity.modules;

  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showExportModal, setShowExportModal] = useState(false);

  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  // `lang` — company names come back in the request locale.
  useEffect(() => {
    dispatch(getApprovedCompaniesRequest());
  }, [dispatch, lang]);

  const moduleOptions = useMemo(
    () =>
      ADMIN_ACTIVITY_MODULES.map((id) => ({ id, name: moduleLabels[id] })),
    [moduleLabels],
  );

  // The current search + filters as API params (no paging).
  const filterParams = useMemo(() => {
    const params = { actor_type: "Admin" };
    if (debouncedSearch) params.query = debouncedSearch;
    if (moduleFilter.length) {
      params.module = moduleFilter
        .map((option) => API_KEY_BY_MODULE[option.id])
        .join(",");
    }
    if (companyFilter) params.company_id = companyFilter.id;
    if (dateRange?.startDate) {
      params.created_at_start_date = dateRange.startDate;
    }
    if (dateRange?.endDate) params.created_at_end_date = dateRange.endDate;
    return params;
  }, [companyFilter, dateRange, debouncedSearch, moduleFilter]);

  // `lang` — company and role names come back in the request locale.
  useEffect(() => {
    dispatch(
      getActivityLogsRequest({ ...filterParams, page, items: pageSize }),
    );
  }, [dispatch, filterParams, lang, page, pageSize]);

  const rows = useMemo(() => list.map((entry) => toActivityLogRow(entry)), [list]);

  // Entries matching the search + filters (across all pages).
  const matchingCount = meta.count;
  const rangeFrom = matchingCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, matchingCount);

  const hasActiveFilters = Boolean(
    search.trim() || moduleFilter.length || companyFilter || dateRange,
  );
  // Nothing logged yet → nothing to search, filter or export: hide the bar.
  // It stays while a search / filter is active, so it can be cleared.
  const showFilters = hasActiveFilters || matchingCount > 0;

  /** Wraps a filter setter so any change goes back to page 1. */
  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setModuleFilter([]);
    setCompanyFilter(null);
    setDateRange(null);
    setPage(1);
  };

  /** Export: every entry matching the current filters (`page=-1`). */
  const loadExportEntries = async () => {
    try {
      const response = await getActivityLogs({ ...filterParams, page: -1 });
      return (response.data?.data || []).map((entry) => toActivityLogRow(entry));
    } catch {
      // The request interceptor already shows the error toast.
      return null;
    }
  };

  // Export modal fields — the table's columns, with the actor split into name
  // and role. `value` reads the cell for one entry.
  const exportFields = useMemo(() => {
    const labels = copy.exportFields;
    return [
      {
        name: "time",
        label: labels.time,
        value: (entry) => formatActivityDateTime(entry.createdAt, lang),
      },
      {
        name: "actorName",
        label: labels.actorName,
        value: (entry) => entry.actor.name || "—",
      },
      {
        name: "actorRole",
        label: labels.actorRole,
        value: (entry) => entry.actor.role || "—",
      },
      { name: "company", label: labels.company, value: (entry) => entry.company || "—" },
      { name: "action", label: labels.action, value: (entry) => entry.action },
      {
        name: "module",
        label: labels.module,
        value: (entry) => moduleLabels[entry.module] || "—",
      },
    ];
  }, [copy.exportFields, lang, moduleLabels]);

  return (
    <div className="activity-log">
      <Breadcrumbs
        list={[
          { path: ROUTE_PATHS.adminDashboard, label: dashboard.breadcrumb },
          { path: "", label: copy.title },
        ]}
        BreadCrumbHasBackIcon={false}
      />

      {showFilters && (
        <Filters
          isCollapsable
          searchInput={{
            placeholder: copy.adminSearchPlaceholder,
            name: "activityLogSearch",
            value: search,
            onChange: onFilterChange(setSearch),
          }}
          renderActionBeforeFilterToggle={
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              size="large"
              disabled={matchingCount === 0}
              onClick={() => setShowExportModal(true)}
            >
              {copy.exportAll}
            </UIButton>
          }
          filters={[
            {
              type: "filter",
              name: "activityLogModule",
              placeholder: copy.filters.module,
              value: moduleFilter,
              options: moduleOptions,
              isMultiple: true,
              onChange: onFilterChange((value) => setModuleFilter(value || [])),
            },
            {
              type: "filter",
              name: "activityLogCompany",
              placeholder: copy.filters.company,
              value: companyFilter,
              options: companies,
              onChange: onFilterChange((value) => setCompanyFilter(value || null)),
            },
            {
              type: "component",
              name: "activityLogDateRange",
              render: () => (
                <DateRangeFilterInput
                  name="activityLogDateRange"
                  placeholder={copy.filters.dateRange}
                  value={dateRange}
                  onChange={onFilterChange(setDateRange)}
                />
              ),
            },
          ]}
          classNames={{
            container: "activity-log__filters",
            base: "",
            search: "mb-3 mb-md-0",
            filter: "",
          }}
        />
      )}

      {matchingCount === 0 ? (
        // Nothing logged yet vs. nothing matching the search / filters.
        !loading && (
          <UIEmptyState
            title={hasActiveFilters ? copy.empty.title : copy.noActivity.title}
            description={
              hasActiveFilters
                ? copy.empty.description
                : copy.noActivity.description
            }
            icon={<ClockArrowDown aria-hidden />}
            actionLabel={hasActiveFilters ? copy.empty.clear : null}
            onAction={hasActiveFilters ? clearFilters : undefined}
          />
        )
      ) : (
        <>
          <ActivityLogTable
            rows={rows}
            labels={copy}
            modules={moduleLabels}
            lang={lang}
          />
          <UIPaginationBar
            currentPage={page}
            pages={meta.pages}
            pageSize={pageSize}
            rangeTemplate={copy.showingRange}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            rangeTotal={matchingCount}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            className="activity-log__pagination"
          />
        </>
      )}

      <ActivityLogExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        fields={exportFields}
        loadEntries={loadExportEntries}
        copy={copy}
        cancelLabel={shared.cancel}
      />
    </div>
  );
};

export default ActivityLog;
